
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import CustomAddIcon from "@/components/icons/CustomAddIcon";
import { useTags, Tag } from "@/hooks/useTags";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { useContentTags } from "@/hooks/useContentTags";
import EditTagDialog from "@/components/tags/EditTagDialog";

interface AddTagButtonProps {
  className?: string;
  variant?: "default" | "outline" | "secondary" | "icon" | "circle";
  id?: string;
  showHoverText?: boolean;
  contentId?: string;
  onAddClick?: () => void;
  existingTags?: Tag[];
  onTagToggle?: (tagId: string, isAdding: boolean) => void;
  forceUseExternalDialog?: boolean;
  isDialogOpen?: boolean;
  onDialogOpenChange?: (open: boolean) => void;
  preventPageReload?: boolean;
}

const AddTagButton = ({ 
  className, 
  variant = "default", 
  id,
  showHoverText = false,
  contentId,
  onAddClick,
  existingTags,
  onTagToggle,
  forceUseExternalDialog = false,
  isDialogOpen,
  onDialogOpenChange,
  preventPageReload = false
}: AddTagButtonProps) => {
  const [internalDialogOpen, setInternalDialogOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isEditTagOpen, setIsEditTagOpen] = useState(false);
  const { tags, isLoading } = useTags();
  const contentTagsHook = contentId ? useContentTags(contentId) : null;
  
  const contentTags = contentTagsHook?.contentTags || existingTags || [];
  const addTagToContent = contentTagsHook?.addTag;
  const removeTagFromContent = contentTagsHook?.removeTag;
  
  const dialogOpen = isDialogOpen !== undefined ? isDialogOpen : internalDialogOpen;
  const setDialogOpen = onDialogOpenChange || setInternalDialogOpen;

  const handleMouseEnter = () => {
    if (showHoverText) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (showHoverText) {
      setIsHovered(false);
    }
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    if (onAddClick && (forceUseExternalDialog || !contentId)) {
      e.stopPropagation();
      onAddClick();
    } else if (!forceUseExternalDialog) {
      e.stopPropagation();
      setDialogOpen(true);
    }
  };

  const handleTagToggle = async (tagId: string) => {
    const isTagApplied = contentTags.some(tag => tag.id === tagId);
    
    if (onTagToggle) {
      onTagToggle(tagId, !isTagApplied);
      return;
    }
    
    if (contentId) {
      if (isTagApplied && removeTagFromContent) {
        await removeTagFromContent(tagId);
      } else if (!isTagApplied && addTagToContent) {
        await addTagToContent(tagId);
      }
    }
  };

  const handleCreateNewTag = () => {
    setDialogOpen(false);
    setIsEditTagOpen(true);
  };

  const handleEditDialogOpenChange = (open: boolean) => {
    setIsEditTagOpen(open);
    
    // Only force refresh when the tag dialog closes after being open
    if (!open && isEditTagOpen && !preventPageReload) {
      console.log("[NAVIGATION DEBUG] AddTagButton - Tag edit dialog closed, refreshing page");
      setTimeout(() => {
        window.location.reload();
      }, 300);
    }
  };

  const isTagApplied = (tagId: string) => {
    return contentTags.some(tag => tag.id === tagId);
  };

  if (className === "hidden") {
    return (
      <>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Manage Tags</DialogTitle>
            </DialogHeader>
            
            <div className="tag-list-container overflow-y-auto py-2">
              {isLoading ? (
                <div className="py-4 text-center text-sm text-gray-500">
                  Loading tags...
                </div>
              ) : tags.length > 0 ? (
                <div className="space-y-1">
                  {tags.map((tag) => (
                    <button
                      key={tag.id}
                      onClick={() => handleTagToggle(tag.id)}
                      className={cn(
                        "w-full flex items-center px-3 py-2 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors",
                        isTagApplied(tag.id) && "bg-gray-50 dark:bg-gray-600"
                      )}
                    >
                      <div 
                        className="w-4 h-4 rounded-full mr-3"
                        style={{ backgroundColor: tag.color }} 
                      />
                      <span className="flex-1 text-left">{tag.name}</span>
                      {isTagApplied(tag.id) && (
                        <Check className="h-4 w-4 text-green-500" />
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="py-4 text-center text-sm text-gray-500">
                  No tags available
                </div>
              )}
            </div>
            
            <Separator className="my-2" />
            
            <div className="flex justify-end">
              <Button 
                variant="outline" 
                onClick={handleCreateNewTag}
              >
                Create New Tag
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <EditTagDialog
          open={isEditTagOpen}
          onOpenChange={handleEditDialogOpenChange}
        />
      </>
    );
  }

  const buttonContent = () => {
    if (variant === "circle") {
      return (
        <div 
          className={`flex items-center ${className}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {isHovered && (
            <span className="text-[#0073FF] text-sm font-medium mr-2">Add</span>
          )}
          <Button 
            variant="ghost" 
            size="icon" 
            className="p-0 h-7 w-7 flex items-center justify-center hover:bg-transparent"
            id={id}
            onClick={handleButtonClick}
          >
            <CustomAddIcon 
              size={24} 
              variant={isHovered ? "blue" : "gray"} 
            />
          </Button>
        </div>
      );
    } else if (variant === "icon") {
      return (
        <Button 
          variant="ghost" 
          size="icon" 
          className={className}
          id={id}
          onClick={handleButtonClick}
        >
          <CustomAddIcon size={24} variant="blue" />
        </Button>
      );
    } else {
      return (
        <Button 
          className={`bg-[#0073FF] hover:bg-[#0060D6] transition-colors flex items-center gap-0 px-3 py-2 ${className}`}
          id={id}
          onClick={handleButtonClick}
        >
          <span className="mr-0">Add Tag</span>
          <CustomAddIcon size={24} variant="blue" className="-ml-[2px]" />
        </Button>
      );
    }
  };

  if (forceUseExternalDialog) {
    return buttonContent();
  }

  return (
    <>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogTrigger asChild>
          {buttonContent()}
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Manage Tags</DialogTitle>
          </DialogHeader>
          
          <div className="tag-list-container overflow-y-auto py-2">
            {isLoading ? (
              <div className="py-4 text-center text-sm text-gray-500">
                Loading tags...
              </div>
            ) : tags.length > 0 ? (
              <div className="space-y-1">
                {tags.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => handleTagToggle(tag.id)}
                    className={cn(
                      "w-full flex items-center px-3 py-2 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors",
                      isTagApplied(tag.id) && "bg-gray-50 dark:bg-gray-600"
                    )}
                  >
                    <div 
                      className="w-4 h-4 rounded-full mr-3"
                      style={{ backgroundColor: tag.color }} 
                    />
                    <span className="flex-1 text-left">{tag.name}</span>
                    {isTagApplied(tag.id) && (
                      <Check className="h-4 w-4 text-green-500" />
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="py-4 text-center text-sm text-gray-500">
                No tags available
              </div>
            )}
          </div>
          
          <Separator className="my-2" />
          
          <div className="flex justify-end">
            <Button 
              variant="outline" 
              onClick={handleCreateNewTag}
            >
              Create New Tag
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <EditTagDialog
        open={isEditTagOpen}
        onOpenChange={handleEditDialogOpenChange}
      />
    </>
  );
};

export default AddTagButton;
